'use client';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { LogIn, UserPlus } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';

interface DirectAuthButtonsProps {
  onItemClick?: (() => void) | undefined;
  /**
   * `'inline'` (default) is the header row: the two buttons side by side, and
   * sign-up drops away below `md` because the row shares its width with the
   * logo and the theme/locale switchers.
   *
   * `'stacked'` is the hamburger sheet, which owns its full width — both
   * buttons are shown at every size and stack vertically. Sign-up must NOT be
   * dropped here: the sheet is the only place a phone-sized viewport can reach
   * it at all (#11039).
   */
  layout?: 'inline' | 'stacked';
}

function DirectAuthButtons({
  onItemClick,
  layout = 'inline',
}: DirectAuthButtonsProps) {
  const stacked = layout === 'stacked';
  const t = useTranslations('Header');
  const router = useRouter();
  const locale = useLocale();

  const signInSlug = locale === 'sk' ? 'prihlasenie' : 'sign-in';
  const signUpSlug = locale === 'sk' ? 'registracia' : 'sign-up';

  return (
    <div
      className={cn(
        'flex gap-2',
        stacked ? 'flex-col items-stretch' : 'items-center'
      )}
    >
      {/* Sign In Button — navigates directly, no intermediate modal */}
      <Button
        variant='brandOutline'
        size='sm'
        className={cn(
          'group flex gap-2 px-4 py-2 max-md:w-full max-md:justify-center',
          stacked && 'w-full justify-center'
        )}
        onClick={() => {
          if (onItemClick) onItemClick();
          router.push(`/${locale}/${signInSlug}`);
        }}
      >
        <LogIn className='h-4 w-4 transition-transform duration-300 group-hover:translate-x-[-2px]' />
        <span className='font-medium text-sm'>{t('signIn')}</span>
      </Button>

      {/* Sign Up Button — navigates directly, no intermediate modal */}
      <div className={cn(stacked ? 'flex' : 'hidden md:flex', 'items-center')}>
        <Button
          variant='brand'
          size='sm'
          className={cn(
            'group flex gap-2 px-4 py-2 max-md:w-full max-md:justify-center',
            stacked && 'w-full justify-center'
          )}
          onClick={() => {
            if (onItemClick) onItemClick();
            router.push(`/${locale}/${signUpSlug}`);
          }}
        >
          <UserPlus className='h-4 w-4 transition-transform duration-300 group-hover:translate-y-[-2px]' />
          <span className='font-medium text-sm'>{t('signUp')}</span>
        </Button>
      </div>
    </div>
  );
}

export default DirectAuthButtons;
